import axios from "axios";
import { omitTrashedTrials } from "./activeTrials";
import { normalizeTrialProcessingStatus } from "./trialStatusDisplay";

function inferTrialTimezone(trial) {
  /* istanbul ignore next — normalizeTrial only passes objects; guard is defensive */
  if (!trial || typeof trial !== 'object') return undefined;
  if (trial.recorded_timezone != null) return trial.recorded_timezone;
  const candidates = ['recordedTimezone', 'recording_timezone', 'recordingTimezone', 'timezone', 'time_zone'];
  for (const key of candidates) {
    if (trial[key] != null) return trial[key];
  }
  return undefined;
}

function inferTrialDate(trial) {
  /* istanbul ignore next — normalizeTrial only passes objects; guard is defensive */
  if (!trial || typeof trial !== 'object') return undefined;
  if (trial.date != null) return trial.date;

  // Support upcoming backend migrations (e.g., timestamptz columns) by
  // mapping whichever timestamp field is present to the UI's `date`.
  const candidates = [
    'recorded_at',
    'recordedAt',
    'created_at',
    'createdAt',
    'trial_timestamp',
    'trialTimestamp',
    'start_time',
    'startTime',
    'timestamp',
    'ts',
    'time',
    'datetime',
    'trial_datetime',
    'trialDatetime',
    'trial_timestampz',
    'trialTimestampz',
  ];

  for (const key of candidates) {
    if (trial[key] != null) return trial[key];
  }

  // As a final fallback (older data), some flows encoded datetime in `name`.
  const name = trial.name;
  if (typeof name === 'string' && /^\d{4}-\d{2}-\d{2}/.test(name.trim())) {
    return name;
  }
  return undefined;
}

function inferTrialQualityFields(trial) {
  /* istanbul ignore next — normalizeTrial only passes objects; guard is defensive */
  if (!trial || typeof trial !== 'object') return {};
  const patch = {};
  const calib = trial.calib_worst_cam_loo_px ?? trial.calib_worst_cam_px
    ?? trial.calibWorstCamLooPx ?? trial.calibWorstCamPx;
  const tracking = trial.pose_cam_support ?? trial.poseCamSupport;
  if (calib != null) patch.calib_worst_cam_loo_px = calib;
  if (tracking != null) patch.pose_cam_support = tracking;
  return patch;
}

function normalizeTrial(trial) {
  if (!trial || typeof trial !== 'object') return trial;
  const inferred = inferTrialDate(trial);
  const tz = inferTrialTimezone(trial);
  const quality = inferTrialQualityFields(trial);
  const patch = { ...quality };
  if (inferred !== undefined) patch.date = inferred;
  if (tz !== undefined) patch.recorded_timezone = tz;
  const withFields = Object.keys(patch).length === 0 ? trial : { ...trial, ...patch };
  return normalizeTrialProcessingStatus(withFields);
}

function normalizeTrialsResponse(res) {
  if (!res || typeof res !== 'object') return res;
  if (Array.isArray(res.result)) {
    return { ...res, result: res.result.map(normalizeTrial) };
  }
  return res;
}

/** Shapes: { result:[] }, { data:[] }, { trials:[] }, { items:[] }, { records:[] }, or top-level []. */
function coalesceTrialsListBody(body) {
  if (body == null) return { result: [] };
  if (Array.isArray(body)) return { result: body };
  if (typeof body === 'object') {
    if (Array.isArray(body.result)) return { result: body.result };
    if (Array.isArray(body.data)) return { result: body.data };
    if (Array.isArray(body.trials)) return { result: body.trials };
    if (Array.isArray(body.items)) return { result: body.items };
    if (Array.isArray(body.records)) return { result: body.records };
  }
  return { result: [] };
}

/** Legacy lab datasets are listed from these twins of the main list routes. */
const LEGACY_ROUTE_PREFIX = "/a/legacy";

function fetchTrialsByPatient(routePrefix, patientID) {
  const token = localStorage.getItem("token");
  return fetch(
    window.SERVER_URL + `${routePrefix}/get-trials-by-patient?patient_id=${patientID}`,
    {
      headers: {
        Authorization: "Bearer " + token,
      },
    }
  )
    .then((res) => res.json())
    .then(normalizeTrialsResponse)
    .then(omitTrashedTrials)
    .catch((err) => console.log(err));
}

export async function getTrials(patientID) {
  return fetchTrialsByPatient("/a", patientID);
}

export async function getLegacyTrials(patientID) {
  return fetchTrialsByPatient(LEGACY_ROUTE_PREFIX, patientID);
}

export async function getAllTrialsByUser(){
  return fetchTrialsByUser("/a");
}

export async function getAllLegacyTrialsByUser() {
  return fetchTrialsByUser(LEGACY_ROUTE_PREFIX);
}

function fetchTrialsByUser(routePrefix) {
  const token = localStorage.getItem("token");
  return fetch(
    window.SERVER_URL + `${routePrefix}/get-trials-by-user`,
    {
      headers: {
        Authorization: "Bearer " + token,
      },
    }
  )
    .then(async (response) => {
      const data = await response.json().catch(function () { return null; });
      if (response.ok === false) {
        return normalizeTrialsResponse({ result: [] });
      }
      const coalesced = coalesceTrialsListBody(data);
      return normalizeTrialsResponse(coalesced);
    })
    .catch((err) => {
      console.log(err);
      return normalizeTrialsResponse({ result: [] });
    });
}
export async function getReports(patientID) {
  const token = localStorage.getItem("token");
  return fetch(window.SERVER_URL + `/a/reports?patient_id=${patientID}`, {
    headers: {
      Authorization: "Bearer " + token,
    },
  })
    .then((res) => res.json())
    .catch((err) => console.log(err));
}
export async function getReport(reportID) {
  const token = localStorage.getItem("token");
  return fetch(window.SERVER_URL + `/a/reports/report?report_id=${reportID}&`, {
    headers: {
      Authorization: "Bearer " + token,
    },
  })
    .then((res) => res.json())
    .catch((err) => console.log(err));
}
export async function getDictMetrics(trialID) {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: "Bearer " + token } : {};
  return fetch(
    window.SERVER_URL + `/a/get-dict-metrics?trial_id=${encodeURIComponent(trialID)}`,
    { headers }
  )
    .then((res) => res.json())
    .catch((err) => console.log(err));
}

export async function getTrialsSummaryReport(trialIDList) {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: "Bearer " + token } : {};
  const trialParams = trialIDList.map((trial_id) => `trial_id=${encodeURIComponent(trial_id)}`);
  const url = "/a/get-trials-by-ids?".concat(trialParams.join("&"));
  return fetch(window.SERVER_URL + url, { headers })
    .then((res) => res.json())
    .catch((err) => console.log(err));
}
export async function createReport(report) {
  const token = localStorage.getItem("token");
  return axios
    .post(
      window.SERVER_URL + `/a/create-report`,
      {
        ...report,
      },
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    )
    .then((res) => res.data)
    .catch((err) => console.log(err));
}
export async function updateReport(report) {
  const token = localStorage.getItem("token");
  return axios
    .put(
      window.SERVER_URL + `/a/update-report`,
      {
        ...report,
      },
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    )
    .then((res) => res.data)
   
}
export async function deleteReport(reportID) {
  const token = localStorage.getItem("token");
  return axios
    .delete(
      window.SERVER_URL + `/a/delete-report?report_id=`+reportID,
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    )
    .then((res) => res.data).catch(e=>console.log(e))
   
}